import {
  CreateDataBlockDto,
  DataBlockWithValuesDto,
  DataType,
  QueryFactory,
  SortDirection,
  useDataBlockType,
} from '@lmt/shared-types';
import {
  DataBlockVariant,
  UIConfigType,
  UpdateDataBlockDto,
} from '@lmt/shared-types';

import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import { UIEditorLazy } from '@lmt/unicorn-shared/app/components/editor/UIEditorLazy';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { defaultUiConfig } from '@lmt/unicorn-shared/app/components/measurements/default-ui-config';
import { createId } from '@lmt/unicorn-shared/app/helpers/create-id';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { parseError } from '@lmt/unicorn-shared/app/helpers/error-handling';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { defaultExcludedFileTypes } from '../patients/profile/cases/components/documents/common';
import {
  DataBlockValues,
  getDataBlockTypeNameForLocalization,
  getUIConfig,
} from './common';
import { UniDetails } from '../common/ui-kit/buttons/UniDetails';
import { getPreviousDataBlockValues } from './copy-datablock-field/copy-previous-values-async-helper';
import {
  customValuesToCase,
  customValuesToPatient,
  customValuesToVisit,
  useCaseValuesForDataBlock,
  usePatientValuesForDataBlock,
  useVisitValuesForDataBlock,
} from './data-helper';
import { UIEditorData, UIEditorDataObjectValue } from '../editor/UIEditorTypes';
import { convertToCurrentTimezone } from '../../helpers/timezone-change-helper';

export type DataBlockUiEditorProps = (
  | { type: 'create'; patientId: number; caseId: number; visitId?: number }
  | { type: 'update_data_block'; dataBlockId: number }
  | { type: 'update_patient_block'; patientId: number }
) & {
  isStatistic?: boolean;
  dataBlockTypeId: number;
  isEditable: boolean;
  setUnsavedChanges?: (value: boolean) => void;
  actions: React.Ref<{
    /*
    Saves the edits made by the user. `isEditable` should be changed by parent component (if needed)!
     */
    save(): Promise<void>;
    /*
    Resets the edits made by the user. `isEditable` should be changed by parent component (if needed)!
     */
    reset(): Promise<void>;
    /*
    Opens the FullView in popup
     */
    openFullView(): void;
    delete(): Promise<void>;
    getValues(): Promise<DataBlockValues>;
  }>;
};

export const DataBlockUiEditor: React.FC<DataBlockUiEditorProps> = (props) => {
  const { dataBlockTypeId, isEditable, actions } = props;
  const i18next = useTranslation();
  const modals = useModal();
  const forms = useGlobalForms();
  const queryClient = useQueryClient();
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const selectedDataBlockType = useDataBlockType(dataBlockTypeId)!;
  const dataBlockFields = QueryFactory.DataBlockFieldQuery.useGetQuery({
    dataBlockTypeId: dataBlockTypeId,
  });
  const dataBlockQuery = QueryFactory.DataBlockQuery.useGetDataBlockQuery(
    props.type === 'update_data_block' ? { id: props.dataBlockId } : null!,
    {
      enabled: props.type === 'update_data_block',
    },
  );
  const dataBlock =
    props.type === 'create'
      ? ({
          visitId: props.visitId,
          patientId: props.patientId,
          caseId: props.caseId,
          typeId: dataBlockTypeId,
        } as DataBlockWithValuesDto)
      : dataBlockQuery.data;
  const caseId = ['update_data_block', 'create'].includes(props.type)
    ? dataBlock?.caseId
    : undefined;
  const visitId = ['update_data_block', 'create'].includes(props.type)
    ? dataBlock?.visitId
    : undefined;
  const patientId =
    props.type === 'update_patient_block' ? props.patientId : undefined;

  const sharedCaseValues = useCaseValuesForDataBlock(caseId);
  const sharedVisitValues = useVisitValuesForDataBlock(visitId);
  const sharedPatientValues = usePatientValuesForDataBlock(patientId);
  const currentSharedValues =
    selectedDataBlockType?.variant === DataBlockVariant.Case
      ? sharedCaseValues
      : selectedDataBlockType?.variant === DataBlockVariant.Visit
        ? sharedVisitValues
        : selectedDataBlockType?.variant === DataBlockVariant.Patient
          ? sharedPatientValues
          : undefined;

  const dataBlockValuesUnchanged = useRef<any>({});
  const dataBlockValuesUnchangedStringified = useRef('{}');

  useEffect(() => {
    setShowErrors(false);
  }, [isEditable]);

  useEffect(() => {
    const stringifiedDataBlockValues = stringifyForComparison(
      dataBlock?.values,
    );
    if (
      props.type !== 'create' &&
      dataBlock &&
      stringifiedDataBlockValues !== dataBlockValuesUnchangedStringified.current
      // we compare received JSON with initial JSON values,
      // and if they are the same, we will not update (since it might be modified by user already)
    ) {
      dataBlockValuesUnchangedStringified.current = stringifiedDataBlockValues;
      dataBlockValuesUnchanged.current = structuredClone(dataBlock.values);
      setDataBlockValues(structuredClone(dataBlockValuesUnchanged.current));
    }
  }, [dataBlock]);

  const sharedValuesUnchanged = useRef<any>({});
  const sharedValuesUnchangedStringified = useRef('{}');
  useEffect(() => {
    const stringifiedSharedValues = stringifyForComparison(currentSharedValues);
    if (
      currentSharedValues &&
      stringifiedSharedValues !== sharedValuesUnchangedStringified.current
      // we compare received JSON with initial JSON values,
      // and if they are the same, we will not update (since it might be modified by user already)
    ) {
      sharedValuesUnchangedStringified.current = stringifiedSharedValues;
      sharedValuesUnchanged.current = structuredClone(currentSharedValues);
      setDataBlockSharedValues(structuredClone(sharedValuesUnchanged.current));
    }
  }, [currentSharedValues]);

  const [dataBlockValues, setDataBlockValues] = useState<UIEditorData>({});
  const [dataBlockValueErrors, setDataBlockValueErrors] = useState<{
    [key: string]: string;
  }>({});
  const [dataBlockSharedValues, setDataBlockSharedValues] =
    useState<UIEditorData>({});

  const configQuery =
    QueryFactory.DataBlockTypeQuery.useGetUIConfigsQuery(dataBlockTypeId);

  const changeDataBlockValues = useCallback(
    (key: string, value: UIEditorDataObjectValue) => {
      setDataBlockValues((prevValues) => {
        return {
          ...prevValues,
          [key]: value,
        };
      });
    },
    [],
  );

  const changeSharedValues = useCallback(
    (key: string, value: UIEditorDataObjectValue) => {
      setDataBlockSharedValues((prevValues) => {
        return {
          ...prevValues,
          [key]: value ?? '',
        };
      });
    },
    [],
  );
  const changeDataBlockValueErrors = useCallback(
    (key: string, error: string) => {
      if (error) {
        setDataBlockValueErrors((prevErrors) => {
          return { ...prevErrors, [key]: error };
        });
      } else {
        setDataBlockValueErrors((prevErrors) => {
          const result = { ...prevErrors };
          delete result[key];
          return result;
        });
      }
    },
    [],
  );
  const [uiEditorKey, setUiEditorKey] = useState('');

  const getDataBlockValues = useCallback(async () => {
    if (Object.keys(dataBlockValueErrors).length > 0) {
      throw new Error('Validation errors');
    }

    return {
      fieldValues: dataBlockValues,
      sharedValues: dataBlockSharedValues,
    };
  }, [dataBlockValues, dataBlockSharedValues, dataBlockValueErrors]);

  useEffect(function CopyValuesFromPreviousBlocks() {
    if (props.type === 'create') {
      getPreviousDataBlockValues(
        queryClient,
        props.patientId,
        props.dataBlockTypeId,
        forms.openCopyDataBlockValue,
        setDataBlockValues,
        setDataBlockSharedValues,
        modals,
        props.caseId,
        props.visitId,
      ).catch((e) => console.error('CopyValuesFromPreviousBlocks', e));
    }
  }, []);

  const saveDataBlockValues = useCallback(async () => {
    if (Object.keys(dataBlockValueErrors).length > 0) {
      throw new Error('Validation errors');
    }

    try {
      if (
        (dataBlockValues && Object.keys(dataBlockValues).length > 0) ||
        dataBlockSharedValues ||
        props.type === 'create'
      ) {
        const fixedDataBlockValues = adjustDateOnlyFields(
          dataBlockValues,
          dataBlockFields.data,
        );
        const fixedDataBlockSharedValues = dataBlockSharedValues;

        // we should only update data block fields if there are some values
        const updatedDataBlock: UpdateDataBlockDto = {
          values: fixedDataBlockValues as any,
        };

        if (
          selectedDataBlockType.variant === DataBlockVariant.Case &&
          fixedDataBlockSharedValues
        ) {
          updatedDataBlock.updateCaseDto = customValuesToCase(
            fixedDataBlockSharedValues,
          );
        }
        if (
          selectedDataBlockType.variant === DataBlockVariant.Visit &&
          fixedDataBlockSharedValues
        ) {
          updatedDataBlock.updateVisitDto = customValuesToVisit(
            fixedDataBlockSharedValues,
          );
        }
        if (props.isStatistic) {
          updatedDataBlock.isStatisticCompleted = true;
        }

        if (props.type === 'update_patient_block') {
          if (selectedDataBlockType.variant === DataBlockVariant.Patient) {
            if (fixedDataBlockSharedValues && patientId) {
              const updatePatientDto = customValuesToPatient(
                fixedDataBlockSharedValues,
              );
              await QueryFactory.PatientClient.editPatient(
                patientId,
                updatePatientDto,
              );
            }
          }
        } else if (props.type === 'update_data_block') {
          const updateDataBlockDto = updatedDataBlock;
          await QueryFactory.DataBlockClient.edit(
            props.dataBlockId,
            updateDataBlockDto,
          );
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.DataBlockQuery.getDataBlockQueryKey(
              props.dataBlockId,
            ),
          });
        } else if (props.type === 'create') {
          if (updatedDataBlock.values === undefined) {
            updatedDataBlock.values = {};
          }
          const updateDataBlockDto = {
            ...updatedDataBlock,
            typeId: props.dataBlockTypeId,
            caseId: props.caseId,
            visitId: props.visitId,
          } as CreateDataBlockDto;
          if (selectedDataBlockType.variant === DataBlockVariant.Measurement) {
            if (updatedDataBlock.values?.Date) {
              updateDataBlockDto.begin = new Date(updatedDataBlock.values.Date);
              updateDataBlockDto.end = new Date(updatedDataBlock.values.Date);
            } else {
              updateDataBlockDto.begin = new Date();
              updateDataBlockDto.end = new Date();
            }
          }
          await QueryFactory.DataBlockClient.create(updateDataBlockDto);
        }
        if (visitId) {
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.DataBlockQuery.getQueryKey(
              null,
              caseId,
              visitId,
              null,
            ),
          });
        }

        await queryClient.invalidateQueries({
          queryKey: QueryFactory.DataBlockQuery.getQueryKey({ caseId }),
        });
      }
      if (selectedDataBlockType?.variant === DataBlockVariant.Case && caseId) {
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.CaseQuery.getCaseQueryKey(caseId),
        });
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.CaseQuery.getCasesQueryKey(patientId),
        });
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.DataBlockQuery.getWithValuesQueryKey({
            typeId: selectedDataBlockType.id,
            patientId: patientId!,
            pagination_Limit: 1,
            sorting_SortField: 'id',
            sorting_SortDirection: SortDirection.Desc,
          }),
        });
      }
    } catch (error) {
      await modals.showError({
        text: convertToErrorString(error, { excludeFieldName: true }),
      });
      // we need to throw, otherwise the DataBlock will switch from edit to view mode
      throw error;
    }
  }, [
    dataBlockValues,
    dataBlock,
    selectedDataBlockType,
    dataBlockSharedValues,
    dataBlockValueErrors,
  ]);

  const onDelete = useCallback(async () => {
    if (props.type !== 'update_data_block') return;

    const isConfirmed = await modals.showConfirm({
      title: i18next.t('data_blocks.confirmation_delete_title'),
      text: i18next.t('data_blocks.confirmation_delete_text'),
      okButtonText: i18next.t('data_blocks.delete_button'),
    });
    if (!isConfirmed) throw new Error('Deletion canceled');
    const includeSharedValues = await modals.showConfirm({
      title: i18next.t('data_blocks.confirmations.include_shared_values.title'),
      text: i18next.t('data_blocks.confirmations.include_shared_values.text'),
      okButtonText: i18next.t(
        'data_blocks.confirmations.include_shared_values.ok_text',
      ),
      cancelButtonText: i18next.t(
        'data_blocks.confirmations.include_shared_values.cancel_text',
      ),
    });
    try {
      await QueryFactory.DataBlockClient.remove(props.dataBlockId, {
        includeSharedValues: !!includeSharedValues,
      });
      if (dataBlockQuery.data) {
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.DataBlockQuery.getQueryKey({
            caseId: dataBlockQuery.data.caseId,
          }),
        });

        if (dataBlockQuery.data.patientId) {
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.FileQuery.searchFilesQueryKey({
              patientId: dataBlockQuery.data.patientId,
              excludeFileType: defaultExcludedFileTypes,
            }),
          });
        }
      }
    } catch (err) {
      await modals.showError({ text: parseError(err) });
      throw err;
    }
  }, [dataBlockQuery.data, selectedDataBlockType]);

  useImperativeHandle(actions, () => {
    return {
      async save(): Promise<void> {
        setShowErrors(true);
        await saveDataBlockValues();
      },
      async reset(): Promise<void> {
        setDataBlockValues(
          dataBlockValuesUnchanged.current
            ? structuredClone(dataBlockValuesUnchanged.current)
            : {},
        );
        setDataBlockSharedValues(
          structuredClone(sharedValuesUnchanged.current),
        );
        // This is needed, otherwise editing in Tables are not reset.
        // They are not reset, because technically situation is identical to when react-query was refetched, but data stays the same
        // In latter case we assume we shouldn't reset edits made by the user.
        setUiEditorKey(createId());
      },
      openFullView() {
        if (props.type === 'update_data_block') {
          forms.openDataBlock(
            props.dataBlockId,
            dataBlockTypeId,
            NaN,
            selectedDataBlockType?.value,
          );
        }
        if (props.type === 'update_patient_block') {
          forms.openDataBlock(
            NaN,
            dataBlockTypeId,
            props.patientId,
            selectedDataBlockType?.value,
          );
        }
      },
      async delete() {
        await onDelete();
      },
      async getValues(): Promise<DataBlockValues> {
        setShowErrors(true);
        return await getDataBlockValues();
      },
    };
  }, [selectedDataBlockType?.value, saveDataBlockValues, onDelete]);
  const dataBlockTypeNameForLocalization = getDataBlockTypeNameForLocalization(
    selectedDataBlockType,
  );

  const unsavedChanges = useMemo(() => {
    if (isEditable) {
      const valuesChanges =
        !!dataBlock &&
        stringifyForComparison(dataBlockValues) !==
          dataBlockValuesUnchangedStringified.current;
      const sharedChanges =
        stringifyForComparison(dataBlockSharedValues) !==
        sharedValuesUnchangedStringified.current;

      // if (valuesChanges) {
      //   console.log(
      //     'qwe',
      //     stringifyForComparison(dataBlockValues),
      //     dataBlockValuesUnchangedStringified.current,
      //   );
      // }
      // if (sharedChanges) {
      //   console.log(
      //     'zxc',
      //     stringifyForComparison(dataBlockSharedValues),
      //     'asd',
      //     sharedValuesUnchangedStringified.current,
      //   );
      // }
      return valuesChanges || sharedChanges;
    }
    return false;
  }, [
    dataBlock,
    dataBlockValues,
    dataBlockSharedValues,
    dataBlockValuesUnchangedStringified.current,
    sharedValuesUnchangedStringified.current,
    isEditable,
  ]);

  useEffect(() => {
    props.setUnsavedChanges?.(unsavedChanges);
  }, [unsavedChanges]);

  return (
    <Loading
      loading={configQuery.isLoading || dataBlockQuery.isLoading}
      flex={true}
    >
      {props.isStatistic ? (
        <>
          <UIEditorLazy
            key={uiEditorKey}
            dataBlockTypeNameForLocalization={dataBlockTypeNameForLocalization}
            dataBlockType={selectedDataBlockType}
            dataBlock={dataBlock}
            isOnlyView={!isEditable}
            isInDesignerMode={false}
            config={
              !configQuery.data
                ? undefined
                : (getUIConfig(configQuery.data, UIConfigType.Statistic) ??
                  defaultUiConfig)
            }
            inputValues={dataBlockValues}
            changeValues={changeDataBlockValues}
            sharedValues={dataBlockSharedValues}
            changeSharedValues={changeSharedValues}
            inputErrors={
              // this is to not show errors until data is loaded
              dataBlockQuery.data ? dataBlockValueErrors : undefined
            }
            changeErrors={changeDataBlockValueErrors}
            showErrors={showErrors}
          />
        </>
      ) : (
        <>
          <UIEditorLazy
            key={uiEditorKey}
            isOnlyView={!isEditable}
            dataBlockTypeNameForLocalization={dataBlockTypeNameForLocalization}
            dataBlockType={selectedDataBlockType}
            dataBlock={dataBlock}
            isInDesignerMode={false}
            config={
              !configQuery.data
                ? undefined
                : (getUIConfig(configQuery.data, UIConfigType.View) ??
                  defaultUiConfig)
            }
            inputValues={dataBlockValues}
            changeValues={changeDataBlockValues}
            sharedValues={dataBlockSharedValues}
            changeSharedValues={changeSharedValues}
            inputErrors={
              // this is to not show errors until data is loaded
              dataBlockQuery.data ? dataBlockValueErrors : undefined
            }
            changeErrors={changeDataBlockValueErrors}
            showErrors={showErrors}
          />
          {configQuery.data?.showDetails && (
            <>
              <UniDetails>
                <UIEditorLazy
                  key={uiEditorKey}
                  dataBlockTypeNameForLocalization={
                    dataBlockTypeNameForLocalization
                  }
                  dataBlockType={selectedDataBlockType}
                  dataBlock={dataBlock}
                  isOnlyView={!isEditable}
                  isInDesignerMode={false}
                  config={
                    !configQuery.data
                      ? undefined
                      : (getUIConfig(configQuery.data, UIConfigType.Details) ??
                        defaultUiConfig)
                  }
                  inputValues={dataBlockValues}
                  changeValues={changeDataBlockValues}
                  sharedValues={dataBlockSharedValues}
                  changeSharedValues={changeSharedValues}
                  inputErrors={
                    // this is to not show errors until data is loaded
                    dataBlockQuery.data ? dataBlockValueErrors : undefined
                  }
                  changeErrors={changeDataBlockValueErrors}
                  showErrors={showErrors}
                />
              </UniDetails>
            </>
          )}
        </>
      )}
    </Loading>
  );
};

function adjustDateOnlyFields(
  dataBlockValues: UIEditorData,
  fields: QueryFactory.DataBlockFieldDto[] | undefined,
) {
  if (!fields) return dataBlockValues;

  const copy = { ...dataBlockValues };

  fields?.forEach((field) => {
    if (field.dataType === DataType.DateOnly) {
    } else if (field.dataType === DataType.Table) {
      const tableData = dataBlockValues[field.name];
      if (!tableData || !Array.isArray(tableData)) return;

      const tableDataCopy = tableData.map((row) => {
        const rowCopy = { ...row };
        field.tableColumns!.forEach((column) => {
          var value = row[column.name];
          if (!value) return;

          if (column.dataType === DataType.DateOnly) {
            rowCopy[column.name] = convertToCurrentTimezone(value);
          }
        });
        return rowCopy;
      });
      copy[field.name] = tableDataCopy;
    }
  });

  return copy;
}
function stringifyForComparison(obj: UIEditorData): string {
  // Bug 55295: Warning when cancel changes, close/change tab with unchanged data block that contains calculated fields
  // When we edit data block empty calculated field is null and after saving calculated field is empty string
  // We replace null's to fix it
  // Another issue is: Bug 57647: Web: Warning for unsaved changes appears when save a data block with radio/checkbox groups without changes
  // when we have empty object initially, but we receive empty strings after the form is opened

  const copy = { ...obj };
  Object.keys(copy).forEach((key) => {
    const value = copy[key];
    if (
      value === null ||
      value === undefined ||
      value === '' ||
      (Array.isArray(value) && value.length === 0)
    )
      delete copy[key];
  });
  return JSON.stringify(copy);
}
